import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (n) => (n ? `+${n.slice(0, 2)} ${n.slice(2)}` : "—");
const cell = "px-3 py-2.5 align-top";
const STATE = {
  ready: ["Approved", "text-emerald-300"], missing: ["None", "text-slate-400"], outdated: ["Outdated (not played)", "text-amber-300"],
};
const btn = "text-xs font-semibold underline disabled:opacity-40";

function Player({ n, draft }) {
  const [url, setUrl] = useState(null);
  const id = `${draft ? "draft" : "approved"}-${n.number_id}`;
  useEffect(() => () => { if (url) { URL.revokeObjectURL(url); } }, [url]);
  const load = async () => {
    try { setUrl(URL.createObjectURL((await api.get(`/platform/shally/greetings/${n.number_id}/audio`, { params: { draft }, responseType: "blob" })).data)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return url ? <audio controls autoPlay src={url} className="h-8 w-52" data-testid={`sg-audio-${id}`} />
    : <button type="button" className={`${btn} text-sky-300`} onClick={load} data-testid={`sg-listen-${id}`}>Listen</button>;
}

function TtsDialog({ target, cost, busy, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!target} onOpenChange={(o) => { if (!o && !busy) { onCancel(); } }}>
      <AlertDialogContent data-testid="sg-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Generate greeting draft?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <div><b>{target?.n.business}</b> · {fmt(target?.n.number)} · voice {target?.n.voice}</div>
              <div className="rounded-md bg-slate-100 p-2 text-slate-800" data-testid="sg-confirm-text">“{target?.text}”</div>
              <div data-testid="sg-confirm-cost">One short paid text-to-speech request ({target?.n.tts_model}), about ${cost?.typical_usd}, at most ${cost?.max_usd}. It is saved as a draft only: nothing plays on calls until you listen and approve it.</div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="sg-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); onConfirm(); }} data-testid="sg-confirm-go">
            {busy ? "Generating…" : "Generate (paid)"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Draft({ n, act }) {
  const d = n.draft;
  if (!d) { return <span className="text-xs text-slate-500" data-testid={`sg-no-draft-${n.number_id}`}>No draft</span>; }
  return (
    <div className="space-y-1.5" data-testid={`sg-draft-${n.number_id}`}>
      <div className="text-xs text-slate-400">{d.source === "tts" ? `Text-to-speech (${d.voice})` : "Uploaded file"} · {d.seconds} s</div>
      <div className="max-w-xs text-xs text-slate-300">“{d.text}”</div>
      <div className="flex items-center gap-3">
        <Player key={d.created_at} n={n} draft />
        <button type="button" className={`${btn} text-emerald-300`} onClick={() => act("approve", n)} data-testid={`sg-approve-${n.number_id}`}>Approve</button>
        <button type="button" className={`${btn} text-rose-300`} onClick={() => act("discard", n)} data-testid={`sg-discard-${n.number_id}`}>Remove draft</button>
      </div>
    </div>
  );
}

function Approved({ n, act }) {
  const [label, cls] = STATE[n.state] || [n.state, ""];
  return (
    <div className="space-y-1.5">
      <span className={cls} data-testid={`sg-state-${n.number_id}`}>{label}</span>
      {n.has_audio && <div className="text-xs text-slate-500">{n.source === "upload" ? "Uploaded" : n.source === "tts" ? "Text-to-speech" : "Recorded"} · {n.seconds} s{n.updated_at ? ` · ${new Date(n.updated_at).toLocaleString("en-IN")}` : ""}</div>}
      {n.has_audio && <div className="flex items-center gap-3"><Player key={n.updated_at} n={n} />
        <button type="button" className={`${btn} text-rose-300`} onClick={() => act("remove", n)} data-testid={`sg-remove-${n.number_id}`}>Remove</button></div>}
      {n.state === "ready" && (
        <label className="flex items-center gap-2 text-xs" data-testid={`sg-activation-${n.number_id}`}>
          <input type="checkbox" checked={!!n.greeting_on} onChange={(e) => act(e.target.checked ? "on" : "off", n)} data-testid={`sg-activation-toggle-${n.number_id}`} />
          <span className={n.greeting_on ? "text-emerald-300" : "text-slate-400"}>{n.greeting_on ? "Plays on calls" : "Not playing on calls"}</span>
        </label>)}
      {n.auto_draft && <div className="text-xs text-slate-500" data-testid={`sg-auto-${n.number_id}`}>Automatic draft: {n.auto_draft}</div>}
    </div>
  );
}

function Create({ n, onTts, onUpload, busy }) {
  const [text, setText] = useState(n.greeting);
  const file = useRef(null);
  useEffect(() => { setText(n.greeting); }, [n.greeting]);
  return (
    <div className="min-w-[260px] space-y-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={300}
        className="w-full rounded-md border border-slate-700 bg-slate-900 p-2 text-sm" data-testid={`sg-text-${n.number_id}`} />
      <div className="flex items-center gap-3">
        <button type="button" disabled={busy || !text.trim()} className={`${btn} text-emerald-300`} onClick={() => onTts(n, text)} data-testid={`sg-tts-${n.number_id}`}>Generate ({n.voice})</button>
        <button type="button" disabled={busy} className={`${btn} text-sky-300`} onClick={() => file.current?.click()} data-testid={`sg-upload-${n.number_id}`}>Upload WAV/MP3</button>
        <input ref={file} type="file" accept=".wav,.mp3,audio/wav,audio/mpeg" className="hidden" data-testid={`sg-file-${n.number_id}`}
          onChange={(e) => { if (e.target.files[0]) { onUpload(n, text, e.target.files[0]); } e.target.value = ""; }} />
      </div>
      <div className="text-xs text-slate-500">Upload: the file should say exactly the text above.</div>
    </div>
  );
}

export default function ShallyGreetings() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = useCallback(() => { api.get("/platform/shally/greetings").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  useEffect(() => { reload(); }, [reload]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); } catch (e) { toast.error(apiErr(e)); }
    setBusy(false); setTarget(null); reload();
  };
  const tts = () => run(() => api.post(`/platform/shally/greetings/${target.n.number_id}/tts`, { confirm: true, text: target.text }), "Draft ready — listen, then approve");
  const upload = (n, text, f) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("text", text);
    run(() => api.post(`/platform/shally/greetings/${n.number_id}/upload`, fd), "Draft ready — listen, then approve");
  };
  const act = (what, n) => {
    const u = `/platform/shally/greetings/${n.number_id}`;
    if (what === "approve") { run(() => api.post(`${u}/approve`), "Greeting approved"); }
    if (what === "on" || what === "off") { run(() => api.post(`${u}/activation`, { on: what === "on" }), what === "on" ? "This number will play its approved greeting" : "This number uses the live greeting"); }
    if (what === "discard") { run(() => api.delete(`${u}/draft`), "Draft removed"); }
    if (what === "remove" && window.confirm("Remove the approved greeting? Calls on this number will use the live greeting again.")) { run(() => api.delete(u), "Approved greeting removed"); }
  };
  const rows = data?.numbers.filter((n) => n.eligible) || [];
  return (
    <div data-testid="pa-shally-greetings">
      <h1 className="font-display text-2xl font-bold">Shally Greetings</h1>
      <p className="mt-1 text-sm text-slate-400">Prerecorded greeting per business number. Create a draft (text-to-speech in the number's voice, or upload a WAV/MP3), listen, then approve. An approved greeting plays the moment a call connects only when the global switch is on AND you switch that number on below. Numbers without one keep the live greeting.</p>
      <div className="mt-3 text-sm" data-testid="sg-flag">Fast greeting switch: {data ? (data.fast_greeting_on ? <span className="text-emerald-300">On</span> : <span className="text-slate-400">Off — approved greetings are stored but not played</span>) : "…"}
        {data && <span className="ml-3 text-slate-400" data-testid="sg-allowlist">Extra numbers from settings: {data.fast_greeting_numbers.length ? data.fast_greeting_numbers.join(", ") : "none"}</span>}</div>
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm" data-testid="sg-table">
          <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>
            {["Business", "Number", "Greeting text", "Draft", "Approved greeting"].map((h) => <th key={h} className={`${cell} font-medium`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.number_id} className="border-t border-slate-800" data-testid={`sg-row-${n.number_id}`}>
                <td className={cell}>{n.business}<div className="text-xs text-slate-500">{n.tenant_id}</div>
                  <div className="text-xs text-slate-500" data-testid={`sg-voice-${n.number_id}`}>{n.voice} · {n.greeting_lang === "hi" ? "Hindi" : "English"}</div></td>
                <td className={`${cell} whitespace-nowrap`}>{fmt(n.number)}</td>
                <td className={cell}><Create n={n} busy={busy} onTts={(x, text) => setTarget({ n: x, text })} onUpload={upload} /></td>
                <td className={cell}><Draft n={n} act={act} /></td>
                <td className={cell}><Approved n={n} act={act} /></td>
              </tr>))}
            {data && !rows.length && <tr><td colSpan={5} className={`${cell} text-center text-slate-500`}>No GPT-Live numbers</td></tr>}
          </tbody>
        </table>
      </div>
      <TtsDialog target={target} cost={data?.cost} busy={busy} onCancel={() => setTarget(null)} onConfirm={tts} />
    </div>
  );
}
